import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapCluster, MapClusteringService } from '../../services/map-clustering.service';
import { VESSEL_SKIN_ASSET } from '../../shared/utils/vessel-skin';

/**
 * Aggregation bubble — a port of the Flutter app's `WaypointClusterMarker`
 * (`lib/features/home_screen/presentation/waypoints/waypoint_cluster.dart`).
 *
 * Mobile draws exactly ONE bubble shape for every aggregate. The web previously
 * had three ("waypoint", "vessel", "mixed") with different diameters, badge
 * rules and badge sources, which is why identical situations rendered
 * differently depending on which engine happened to claim the group.
 *
 * Geometry below is 1:1 with the Dart widget:
 *   bubble 42px · boat watermark 32px @ 0.55 · pin sizes 26/20/17 for 1/2/3
 *   icons · x-offsets [0] / [-9,9] / [-10,0,10] · badge when count ≥ 2.
 */
@Component({
  selector: 'app-cluster-marker',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './cluster-marker.component.scss',
  template: `
    <div
      class="cluster-marker-shell"
      [style.width.px]="shellSize"
      [style.height.px]="shellSize"
      (click)="onClusterClick()"
    >
      <div class="cluster-marker" [attr.aria-label]="ariaLabel">
        <div class="cluster-bubble" [style.left.px]="bubbleOffset" [style.top.px]="bubbleOffset"></div>

        <!-- Clipped content: boat watermark under the preview pins -->
        <div class="cluster-clip" [style.left.px]="bubbleOffset" [style.top.px]="bubbleOffset">
          <img
            *ngIf="cluster.hasVessel"
            class="cluster-vessel-watermark"
            [src]="vesselIconUrl"
            alt=""
          />
          <!-- Painted back-to-front so previewIcons[0] ends up topmost, as in Dart -->
          <img
            *ngFor="let pin of pins"
            class="cluster-pin"
            [src]="pin.src"
            [style.left.px]="pin.left"
            [style.top.px]="pin.top"
            [style.width.px]="pin.size"
            [style.height.px]="pin.size"
            [style.z-index]="pin.zIndex"
            alt=""
          />
        </div>

        <div *ngIf="showBadge" class="cluster-badge" [style.left.px]="badgeLeft" [style.top.px]="badgeTop" aria-hidden="true">
          <span class="cluster-badge-text">{{ badgeText }}</span>
        </div>
      </div>
    </div>
  `,
})
export class ClusterMarkerComponent {
  @Input({ required: true }) cluster!: MapCluster;
  @Output() clusterClick = new EventEmitter<{ lat: number; lng: number; count: number }>();

  /** Flutter: `const double bubbleSize = 42.0;` */
  static readonly BUBBLE_PX = 42;
  /** Shell is padded so the overflowing badge is never clipped. */
  static readonly SHELL_PX = 56;

  private static readonly GENERIC_VESSEL_ICON = '/assets/common_icons/boat_other_user.png';

  /** Unanimous hull when every absorbed boat agrees, else the generic boat (as Flutter). */
  get vesselIconUrl(): string {
    const skin = this.cluster?.vesselSkin;
    return skin ? VESSEL_SKIN_ASSET[skin] : ClusterMarkerComponent.GENERIC_VESSEL_ICON;
  }

  // Keep host element at (0,0) in MapLibre's container so transform is the sole
  // source of position. Without this, position:relative elements stack in flow
  // and the translate(-50%,-50%) offsets compound, causing visible drift at the
  // top of the map where expected Y is small.
  @HostBinding('style.position') readonly hostPosition = 'absolute';
  @HostBinding('style.top') readonly hostTop = '0';
  @HostBinding('style.left') readonly hostLeft = '0';
  @HostBinding('style.width.px') get hostWidth(): number { return this.shellSize; }
  @HostBinding('style.height.px') get hostHeight(): number { return this.shellSize; }

  constructor(private clusteringService: MapClusteringService) { }

  get shellSize(): number { return ClusterMarkerComponent.SHELL_PX; }

  /** Bubble is centred inside the padded shell. */
  get bubbleOffset(): number {
    return (ClusterMarkerComponent.SHELL_PX - ClusterMarkerComponent.BUBBLE_PX) / 2;
  }

  get previewIcons(): string[] {
    return (this.cluster?.previewIcons || []).filter(Boolean).slice(0, 3);
  }

  /**
   * Pin layout, matching the Dart widget exactly. Dart paints indices n-1 → 0 so
   * that previewTypes[0] lands on top; in the DOM we keep source order and
   * express the same stacking with z-index.
   */
  get pins(): Array<{ src: string; left: number; top: number; size: number; zIndex: number }> {
    const icons = this.previewIcons;
    const n = Math.max(1, Math.min(icons.length, 3));
    if (!icons.length) return [];

    const size = n === 1 ? 26 : n === 2 ? 20 : 17;
    const xOffsets = n === 1 ? [0] : n === 2 ? [-9, 9] : [-10, 0, 10];
    const half = ClusterMarkerComponent.BUBBLE_PX / 2;

    return icons.slice(0, n).map((src, i) => ({
      src: this.resolveIconSrc(src),
      left: half + xOffsets[i] - size / 2,
      top: half - size / 2,
      size,
      zIndex: 10 - i,
    }));
  }

  /** Flutter: `if (count >= 2)`. */
  get showBadge(): boolean {
    return (this.cluster?.count ?? 0) >= 2;
  }

  get badgeText(): string {
    return this.clusteringService.formatCountBadge(this.cluster?.count ?? 0);
  }

  /** Flutter: `Positioned(left: bubbleSize - 8, top: -5)` relative to the bubble. */
  get badgeLeft(): number {
    return this.bubbleOffset + ClusterMarkerComponent.BUBBLE_PX - 8;
  }

  get badgeTop(): number {
    return this.bubbleOffset - 5;
  }

  get ariaLabel(): string {
    const c = this.cluster;
    if (!c) return 'cluster';
    const parts: string[] = [];
    if (c.waypointCount) parts.push(`${c.waypointCount} waypoint${c.waypointCount === 1 ? '' : 's'}`);
    if (c.vesselCount) parts.push(`${c.vesselCount} vessel${c.vesselCount === 1 ? '' : 's'}`);
    return parts.length ? parts.join(', ') : 'cluster';
  }

  resolveIconSrc(icon: string): string {
    if (!icon) return '';
    let src = icon.startsWith('/') || icon.startsWith('http') ? icon : `/${icon}`;
    src = src.replace(/^\/\//, '/');
    return src.toLowerCase().endsWith('.svg') ? `${src}?_v=2` : src;
  }

  onClusterClick(): void {
    this.clusterClick.emit({
      lat: this.cluster.centerLat,
      lng: this.cluster.centerLng,
      count: this.cluster.count,
    });
  }
}
